class CartNew extends HTMLElement {
  constructor() {
    super();
    this.processing = new Set();
    // Track previous discount state to detect changes
    this.previousDiscountState = undefined;
    this.init();
  }

  init() {
    this.bindEvents();
    this.setupCartChangeListener();
    // Initialize discount state on page load
    this.initializeDiscountState();
  }

  async initializeDiscountState() {
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        const hasDiscounts = cartData.items && cartData.items.some(item => 
          item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0
        ) || (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0);
        this.previousDiscountState = hasDiscounts;
      }
    } catch (error) {
      console.warn('Failed to initialize discount state:', error);
      this.previousDiscountState = false;
    }
  }

  bindEvents() {
    // Quantity buttons
    this.addEventListener('click', (e) => {
      const decreaseBtn = e.target.closest('.cart-new__quantity-btn--decrease');
      const increaseBtn = e.target.closest('.cart-new__quantity-btn--increase');
      const removeBtn = e.target.closest('.cart-new__remove-btn');

      if (decreaseBtn) {
        e.preventDefault();
        this.handleQuantityChange(decreaseBtn.dataset.lineKey, 'decrease');
      } else if (increaseBtn) {
        e.preventDefault();
        this.handleQuantityChange(increaseBtn.dataset.lineKey, 'increase');
      } else if (removeBtn) {
        e.preventDefault();
        this.handleRemoveItem(removeBtn.dataset.lineKey);
      }
    });

    // Quantity input changes
    this.addEventListener('change', (e) => {
      if (e.target.classList.contains('cart-new__quantity-input')) {
        const lineKey = e.target.dataset.lineKey;
        const newQuantity = parseInt(e.target.value) || 1;
        this.handleQuantityChange(lineKey, 'set', newQuantity);
      }
    });
  }

  setupCartChangeListener() {
    // Listen for cart changes from other components
    this.cartChangeHandler = async (event) => {
      if (event.detail && event.detail.cart) {
        // Don't update if we're already processing a change
        if (this.processing.size > 0) {
          return;
        }
        
        // If discount was applied or removed, reload page to show badges and prices correctly
        if (event.detail.baseEvent === 'discount:applied' || event.detail.baseEvent === 'discount:removed') {
          window.location.reload();
          return;
        } else {
          await this.updateCartDisplay(event.detail.cart);
        }
      }
    };
    
    document.addEventListener('cart:change', this.cartChangeHandler);

    // Also listen for cart:updated events
    this.cartUpdatedHandler = async (event) => {
      if (event.detail && event.detail.cart) {
        // Don't update if we're already processing a change
        if (this.processing.size > 0) {
          return;
        }
        
        // Check if this update includes discounts by looking at line items
        const hasDiscounts = event.detail.cart.items && event.detail.cart.items.some(item => 
          item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0
        ) || (event.detail.cart.cart_level_discount_applications && event.detail.cart.cart_level_discount_applications.length > 0);
        
        // Check if discounts status changed (applied or removed)
        // Store previous discount state if not exists
        if (this.previousDiscountState === undefined) {
          this.previousDiscountState = hasDiscounts;
        }
        
        // If discount status changed (applied or removed), reload page
        if (this.previousDiscountState !== hasDiscounts) {
          this.previousDiscountState = hasDiscounts;
          window.location.reload();
          return;
        }
        
        // Otherwise, update normally
        await this.updateCartDisplay(event.detail.cart);
      }
    };
    
    document.addEventListener('cart:updated', this.cartUpdatedHandler);
  }

  async handleQuantityChange(lineKey, action, quantity = null) {
    // Validate lineKey
    if (!lineKey || typeof lineKey !== 'string') {
      console.error('Invalid lineKey:', lineKey);
      return;
    }
    
    if (this.processing.has(lineKey)) {
      console.warn('Already processing item:', lineKey);
      return;
    }
    
    const itemRow = this.querySelector(`[data-line-key="${lineKey}"]`);
    if (!itemRow) {
      console.error('Item row not found for lineKey:', lineKey);
      return;
    }

    // Find all inputs for this item (desktop and mobile)
    const allInputs = itemRow.querySelectorAll('.cart-new__quantity-input');
    if (allInputs.length === 0) {
      console.error('No quantity inputs found for item:', lineKey);
      return;
    }

    // Use first input to get current quantity
    const input = allInputs[0];
    const currentQuantity = parseInt(input.value) || 1;
    let newQuantity = currentQuantity;

    if (action === 'decrease') {
      newQuantity = Math.max(1, currentQuantity - 1);
    } else if (action === 'increase') {
      const maxQuantity = input.max ? parseInt(input.max) : null;
      newQuantity = maxQuantity ? Math.min(maxQuantity, currentQuantity + 1) : currentQuantity + 1;
    } else if (action === 'set' && quantity !== null) {
      newQuantity = Math.max(1, quantity);
      if (input.max) {
        newQuantity = Math.min(parseInt(input.max), newQuantity);
      }
    }

    if (newQuantity === currentQuantity) return;

    // Update all inputs immediately for better UX
    allInputs.forEach(inp => {
      inp.value = newQuantity;
    });

    this.processing.add(lineKey);
    itemRow.setAttribute('data-loading', 'true');

    try {
      // Validate lineKey before sending
      if (!lineKey || lineKey.trim() === '') {
        throw new Error('Invalid lineKey: empty or null');
      }
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: newQuantity
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        
        // Temporarily remove listeners to prevent double updates
        if (this.cartChangeHandler) {
          document.removeEventListener('cart:change', this.cartChangeHandler);
        }
        if (this.cartUpdatedHandler) {
          document.removeEventListener('cart:updated', this.cartUpdatedHandler);
        }
        
        // Use manual update (Sections API can be unreliable)
        // Sections API will be used as fallback if items are not found
        try {
          await this.updateCartDisplay(cartData);
        } catch (updateError) {
          console.warn('Manual update failed, trying Sections API:', updateError);
          // Fallback to Sections API
          try {
            await this.refreshCartSection();
            // After refresh, update totals from cart data
            this.updateCartTotals(cartData);
          } catch (sectionsError) {
            console.error('Both update methods failed:', sectionsError);
            // Last resort - reload page
            window.location.reload();
          }
        }
        
        // Re-add listeners after a short delay
        setTimeout(() => {
          if (this.cartChangeHandler) {
            document.addEventListener('cart:change', this.cartChangeHandler);
          }
          if (this.cartUpdatedHandler) {
            document.addEventListener('cart:updated', this.cartUpdatedHandler);
          }
        }, 100);
        
        // Trigger cart change event for other components (but not for ourselves)
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { cart: cartData }
        }));
      } else {
        // Revert all input values on error
        allInputs.forEach(inp => {
          inp.value = currentQuantity;
        });
        
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { status: response.status, message: 'Unknown error' };
        }
        
        console.error('Error updating cart:', errorData);
        
        // If it's a bad request with invalid id, try to refresh and get correct keys
        if (response.status === 400 && errorData.message && (errorData.message.includes('id') || errorData.message.includes('line'))) {
          console.warn('Invalid lineKey detected, refreshing cart section to get correct keys...');
          try {
            await this.refreshCartSection();
            // After refresh, lineKey should be correct now
            console.log('Cart section refreshed. Please try again.');
          } catch (refreshError) {
            console.error('Failed to refresh cart section:', refreshError);
            // Last resort - reload page
            window.location.reload();
          }
        }
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      // Revert all input values on error
      allInputs.forEach(inp => {
        inp.value = currentQuantity;
      });
    } finally {
      this.processing.delete(lineKey);
      itemRow.removeAttribute('data-loading');
    }
  }

  async handleRemoveItem(lineKey) {
    if (this.processing.has(lineKey)) return;
    
    const itemRow = this.querySelector(`[data-line-key="${lineKey}"]`);
    if (!itemRow) return;

    this.processing.add(lineKey);
    itemRow.setAttribute('data-loading', 'true');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0
        })
      });

      if (response.ok) {
        const cartData = await response.json();
        
        // If cart is empty after removal, reload the page
        if (!cartData || cartData.item_count === 0) {
          window.location.reload();
          return;
        }
        
        // Temporarily remove listeners to prevent double updates
        if (this.cartChangeHandler) {
          document.removeEventListener('cart:change', this.cartChangeHandler);
        }
        if (this.cartUpdatedHandler) {
          document.removeEventListener('cart:updated', this.cartUpdatedHandler);
        }
        
        // Use manual update first, then fallback to Sections API if needed
        try {
          await this.updateCartDisplay(cartData);
        } catch (updateError) {
          console.warn('Manual update failed, trying Sections API:', updateError);
          // Fallback to Sections API
          try {
            await this.refreshCartSection();
            // After refresh, update totals from cart data
            this.updateCartTotals(cartData);
          } catch (sectionsError) {
            console.error('Both update methods failed:', sectionsError);
            // Last resort - reload page
            window.location.reload();
          }
        }
        
        // Re-add listeners after a short delay
        setTimeout(() => {
          if (this.cartChangeHandler) {
            document.addEventListener('cart:change', this.cartChangeHandler);
          }
          if (this.cartUpdatedHandler) {
            document.addEventListener('cart:updated', this.cartUpdatedHandler);
          }
        }, 100);
        
        // Trigger cart change event for other components
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { cart: cartData }
        }));
      } else {
        const errorData = await response.json();
        console.error('Error removing item:', errorData);
        itemRow.removeAttribute('data-loading');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      itemRow.removeAttribute('data-loading');
    } finally {
      this.processing.delete(lineKey);
    }
  }

  async updateCartDisplay(cartData) {
    // If cart is empty, reload page
    if (!cartData || cartData.item_count === 0) {
      window.location.reload();
      return;
    }

    // Initialize needsFullRefresh flag if not exists
    if (this.needsFullRefresh === undefined) {
      this.needsFullRefresh = false;
    }

    // Try manual update first
    try {
      this.updateCartItems(cartData.items);
      this.updateCartTotals(cartData);
      this.updateFreeShipping(cartData);
    } catch (error) {
      // If any item is not found, use Sections API to refresh entire section
      if (error.message && error.message.includes('not found')) {
        console.warn('Item not found, refreshing via Sections API:', error.message);
        await this.refreshCartSection();
        // After refresh, update totals from cart data
        this.updateCartTotals(cartData);
      } else {
        console.error('Error updating cart display:', error);
        // Try refreshing via Sections API as fallback
        await this.refreshCartSection();
      }
    }
  }

  async refreshCartSection() {
    try {
      // Get section ID - normalize it to base name
      let sectionId = 'main-cart-new'; // default fallback
      
      // Method 1: Get from parent section element
      const sectionElement = this.closest('[id^="shopify-section-"]');
      if (sectionElement) {
        let sectionIdFromElement = sectionElement.id.replace('shopify-section-', '');
        
        // Normalize section ID - remove template prefix and hash suffix
        // e.g., "template--20698680295649__main_cart_new_LP3zV6" -> "main_cart_new"
        // or "main-cart-new" -> "main-cart-new"
        if (sectionIdFromElement.includes('__')) {
          // Extract part after "__"
          const parts = sectionIdFromElement.split('__');
          if (parts.length > 1) {
            // Take the part before the last underscore and hash (e.g., "main_cart_new_LP3zV6" -> "main_cart_new")
            let basePart = parts[1];
            // Remove trailing hash/random string (everything after last underscore if followed by letters/numbers)
            basePart = basePart.replace(/_[A-Za-z0-9]+$/, '');
            // Convert underscores to hyphens if needed
            sectionIdFromElement = basePart.replace(/_/g, '-');
          }
        }
        
        // If it contains "cart" or "main", use it (after normalization)
        if (sectionIdFromElement && (sectionIdFromElement.includes('cart') || sectionIdFromElement.includes('main'))) {
          sectionId = sectionIdFromElement;
        }
      }
      
      // Method 2: Get from sectionId property if set (but normalize it too)
      if (this.sectionId) {
        let normalizedId = this.sectionId;
        // Normalize similar to above
        if (normalizedId.includes('__')) {
          const parts = normalizedId.split('__');
          if (parts.length > 1) {
            normalizedId = parts[1].replace(/_[A-Za-z0-9]+$/, '').replace(/_/g, '-');
          }
        }
        if (normalizedId && (normalizedId.includes('cart') || normalizedId.includes('main'))) {
          sectionId = normalizedId;
        }
      }
      
      // Ensure we have a valid section ID - fallback to main-cart-new
      if (!sectionId || (!sectionId.includes('cart') && !sectionId.includes('main'))) {
        sectionId = 'main-cart-new';
      }
      
      // Fetch updated section HTML
      const response = await fetch(`/cart?sections=${sectionId}`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        console.error('Failed to fetch section:', response.status);
        throw new Error(`Failed to fetch section: ${response.status}`);
      }

      const data = await response.json();
      
      // Check if response has sections
      if (!data.sections || Object.keys(data.sections).length === 0) {
        console.warn('No sections in response. Cart might be empty or page needs reload.');
        // If cart is empty, reload page
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          if (!cartData || cartData.item_count === 0) {
            window.location.reload();
            return;
          }
        }
        throw new Error(`No sections found in response. Requested: ${sectionId}`);
      }
      
      // Try to find section HTML with different possible keys
      let newSectionHTML = data.sections?.[sectionId];
      
      // If not found, try variations of the section ID
      if (!newSectionHTML) {
        // Try with underscores instead of hyphens
        const sectionIdWithUnderscores = sectionId.replace(/-/g, '_');
        newSectionHTML = data.sections?.[sectionIdWithUnderscores];
        if (newSectionHTML) {
          console.log('Found section with underscores:', sectionIdWithUnderscores);
        }
      }
      
      // If still not found, try other possible section IDs
      if (!newSectionHTML && data.sections) {
        const possibleKeys = Object.keys(data.sections);
        
        // Try to find any section that contains "cart" or "main"
        const cartSectionKey = possibleKeys.find(key => 
          key.toLowerCase().includes('cart') || key.toLowerCase().includes('main')
        );
        
        if (cartSectionKey) {
          newSectionHTML = data.sections[cartSectionKey];
          console.log('Using cart-related section key:', cartSectionKey);
        } else if (possibleKeys.length > 0) {
          // Use first available section as last resort
          newSectionHTML = data.sections[possibleKeys[0]];
          console.log('Using first available section key:', possibleKeys[0]);
        }
      }

      if (!newSectionHTML) {
        const availableSections = Object.keys(data.sections || {});
        const errorMsg = `Section HTML not found. Requested: ${sectionId}, Available: ${availableSections.join(', ')}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }

      // Parse and update
      const parser = new DOMParser();
      const doc = parser.parseFromString(newSectionHTML, 'text/html');
      const newCartSection = doc.querySelector('cart-new-section') || doc.querySelector('.cart-new');
      
      if (!newCartSection) {
        console.error('Cart section not found in new HTML');
        throw new Error('Cart section not found in new HTML');
      }

      // Update items table
      const newTbody = newCartSection.querySelector('#cart-new-items');
      const currentTbody = this.querySelector('#cart-new-items');
      
      if (!newTbody || !currentTbody) {
        throw new Error('Items table not found in new or current section');
      }
      
      // Store line keys from new HTML before replacing
      const newItemRows = newCartSection.querySelectorAll('.cart-new__item, tr[data-line-key]');
      const lineKeysMap = new Map();
      
      newItemRows.forEach((newRow, index) => {
        const newInput = newRow.querySelector('.cart-new__quantity-input');
        if (newInput) {
          const lineKey = newInput.getAttribute('data-line-key');
          if (lineKey) {
            lineKeysMap.set(index, lineKey);
          }
        }
      });

      // Replace tbody content
      currentTbody.innerHTML = newTbody.innerHTML;

      // Update sidebar totals
      const newSidebar = newCartSection.querySelector('.cart-new__sidebar');
      const currentSidebar = this.querySelector('.cart-new__sidebar');
      
      if (newSidebar && currentSidebar) {
        // Update totals
        const newTotals = newSidebar.querySelector('.cart-new__totals');
        const currentTotals = currentSidebar.querySelector('.cart-new__totals');
        if (newTotals && currentTotals) {
          currentTotals.innerHTML = newTotals.innerHTML;
        }
        
        // Update free shipping
        const newFreeShipping = newSidebar.querySelector('#cart-new-free-shipping');
        const currentFreeShipping = currentSidebar.querySelector('#cart-new-free-shipping');
        if (newFreeShipping && currentFreeShipping) {
          currentFreeShipping.innerHTML = newFreeShipping.innerHTML;
        }
        
        // Update discount component
        const newDiscount = newSidebar.querySelector('.cart-new__discount');
        const currentDiscount = currentSidebar.querySelector('.cart-new__discount');
        if (newDiscount && currentDiscount) {
          currentDiscount.innerHTML = newDiscount.innerHTML;
        }
      }

      // Now update all data-line-key attributes in the current DOM with correct keys
      const currentItemRows = this.querySelectorAll('.cart-new__item, tr[data-line-key]');
      
      currentItemRows.forEach((currentRow, index) => {
        const correctLineKey = lineKeysMap.get(index);
        if (!correctLineKey) return;
        
        // Update row's data-line-key
        currentRow.setAttribute('data-line-key', correctLineKey);
        
        // Update all inputs in this row
        const currentInputs = currentRow.querySelectorAll('.cart-new__quantity-input');
        currentInputs.forEach(input => {
          input.setAttribute('data-line-key', correctLineKey);
          // Also update the name attribute if it exists
          if (input.name) {
            input.name = `updates[${correctLineKey}]`;
          }
        });
        
        // Update all buttons in this row
        const currentButtons = currentRow.querySelectorAll('.cart-new__quantity-btn, .cart-new__remove-btn');
        currentButtons.forEach(btn => {
          btn.setAttribute('data-line-key', correctLineKey);
        });
      });

      // Reinitialize controls - this is critical for event listeners
      this.reinitializeQuantityControls();
      
      // Reinitialize cart discount component if needed
      const discountComponent = this.querySelector('cart-discount-component');
      if (discountComponent && typeof discountComponent.init === 'function') {
        discountComponent.init();
      }
      
      // Format all prices to remove trailing zeros
      this.formatAllPrices();
    } catch (error) {
      console.error('Error refreshing cart section:', error);
    }
  }

  formatAllPrices() {
    // Find all price elements and remove all decimal parts
    const priceSelectors = [
      '.cart-new__price-regular',
      '.cart-new__price-sale',
      '.cart-new__price-compare',
      '.cart-new__line-total-price',
      '#cart-new-subtotal',
      '#cart-new-discount',
      '#cart-new-total'
    ];

    priceSelectors.forEach(selector => {
      const elements = this.querySelectorAll(selector);
      elements.forEach(el => {
        if (el.textContent) {
          let text = el.textContent.trim();
          // Remove all decimal parts (everything after the dot including the dot itself)
          // Match pattern: digit(s) followed by dot and any digits, then optional space/currency/end
          text = text.replace(/(\d+)\.\d+/g, '$1');
          el.textContent = text;
        }
      });
    });
  }

  updateCartItems(items) {
    if (!items || items.length === 0) {
      // If no items, check if cart is really empty or just temporary state
      // Use Sections API to get accurate state
      this.refreshCartSection().catch(() => {
        // If refresh fails and cart is really empty, reload
        window.location.reload();
      });
      return;
    }

    // First, ensure all items exist in the DOM
    const tbody = this.querySelector('#cart-new-items');
    if (!tbody) return;

    const existingLineKeys = new Set();
    const itemsToUpdate = new Map();
    
    // Collect all items that need to be updated
    items.forEach(item => {
      itemsToUpdate.set(item.key, item);
      existingLineKeys.add(item.key);
    });

    // Debug: collect all existing keys in DOM
    const allExistingRows = Array.from(tbody.querySelectorAll('tr.cart-new__item, .cart-new__item'));
    const existingKeysInDOM = allExistingRows.map(row => row.getAttribute('data-line-key')).filter(Boolean);
    
    // Track which rows have been matched to avoid double matching
    const matchedRows = new Set();
    
    // Update existing items or create new ones
    items.forEach((item, itemIndex) => {
      if (!item || !item.key) {
        console.warn('Invalid item in cart data:', item);
        return;
      }

      let itemRow = null;

      // Method 1: Try direct search by data-line-key
      itemRow = tbody.querySelector(`tr[data-line-key="${item.key}"]`) ||
                tbody.querySelector(`.cart-new__item[data-line-key="${item.key}"]`);
      
      // Method 2: If not found, search in entire section
      if (!itemRow) {
        itemRow = this.querySelector(`tr[data-line-key="${item.key}"]`) ||
                  this.querySelector(`.cart-new__item[data-line-key="${item.key}"]`);
      }
      
      // Method 3: Try to find by index position (if counts match and row not matched)
      if (!itemRow && items.length === allExistingRows.length && itemIndex < allExistingRows.length) {
        const candidateRow = allExistingRows[itemIndex];
        // Check if this row hasn't been matched yet
        if (!matchedRows.has(candidateRow)) {
          itemRow = candidateRow;
          matchedRows.add(itemRow);
        }
      }
      
      // Method 4: Try to find any unmatched row by variant_id
      if (!itemRow && item.variant_id) {
        for (const row of allExistingRows) {
          if (matchedRows.has(row)) continue;
          
          // Check if row contains info that might match this variant
          const inputs = row.querySelectorAll('.cart-new__quantity-input');
          if (inputs.length > 0) {
            // This is a valid row, try using it
            itemRow = row;
            matchedRows.add(itemRow);
            break;
          }
        }
      }
      
      if (!itemRow) {
        // Item not found - this happens when DOM structure changes after discount application
        // Throw error to trigger Sections API refresh in parent function
        console.warn(`Item ${item.key} not found in DOM. Items: ${items.length}, Rows: ${allExistingRows.length}`);
        throw new Error(`Item ${item.key} not found - need full refresh`);
      }
      
      // Mark this row as matched
      matchedRows.add(itemRow);
      
      // Update the data attribute to ensure it matches
      if (itemRow.getAttribute('data-line-key') !== item.key) {
        itemRow.setAttribute('data-line-key', item.key);
      }
      
      // Ensure the row has the correct data attribute
      if (!itemRow.hasAttribute('data-line-key')) {
        itemRow.setAttribute('data-line-key', item.key);
      }

      // Update quantity - update ALL inputs for this item (desktop and mobile)
      const allInputs = itemRow.querySelectorAll('.cart-new__quantity-input');
      allInputs.forEach(input => {
        input.value = item.quantity;
        input.setAttribute('data-line-key', item.key);
      });

      // Update buttons state - update ALL buttons for this item (desktop and mobile)
      const allDecreaseBtns = itemRow.querySelectorAll('.cart-new__quantity-btn--decrease');
      const allIncreaseBtns = itemRow.querySelectorAll('.cart-new__quantity-btn--increase');
      
      allDecreaseBtns.forEach(decreaseBtn => {
        decreaseBtn.disabled = item.quantity <= 1;
        decreaseBtn.setAttribute('data-line-key', item.key);
      });
      
      allIncreaseBtns.forEach(increaseBtn => {
        const input = itemRow.querySelector('.cart-new__quantity-input');
        if (input && input.max) {
          increaseBtn.disabled = item.quantity >= parseInt(input.max);
        } else {
          increaseBtn.disabled = false;
        }
        increaseBtn.setAttribute('data-line-key', item.key);
      });

      // Update remove button
      const removeBtn = itemRow.querySelector('.cart-new__remove-btn');
      if (removeBtn) {
        removeBtn.setAttribute('data-line-key', item.key);
      }

      // Update line total
      const lineTotalEl = itemRow.querySelector('.cart-new__line-total');
      if (lineTotalEl) {
        // Remove existing price elements
        const existingPrice = lineTotalEl.querySelector('.cart-new__line-total-price');
        const existingComparePrice = lineTotalEl.querySelector('.cart-new__line-total-price-compare');
        
        if (existingPrice) existingPrice.remove();
        if (existingComparePrice) existingComparePrice.remove();
        
        // Always show regular price without compare price
        const regularPrice = document.createElement('span');
        regularPrice.className = 'cart-new__line-total-price';
        regularPrice.textContent = this.formatMoney(item.final_line_price);
        lineTotalEl.appendChild(regularPrice);
      }

      // Update product price - handle discounts and compare price
      const priceContainer = itemRow.querySelector('.cart-new__product-price');
      if (!priceContainer) return;
      
      const priceSale = itemRow.querySelector('.cart-new__price-sale');
      const priceRegular = itemRow.querySelector('.cart-new__price-regular');
      const priceCompare = itemRow.querySelector('.cart-new__price-compare');
      let discountBadge = itemRow.querySelector('.cart-new__discount-badge');
      
      // Get compare price from existing DOM element or data attribute
      // This preserves compare_at_price which might not be in cart.js response
      let savedComparePrice = null;
      
      // First, check if item row has compare price stored in data attribute
      const comparePriceFromRow = itemRow.getAttribute('data-compare-price');
      if (comparePriceFromRow) {
        savedComparePrice = parseInt(comparePriceFromRow);
      }
      
      // Also check compare price element data attribute
      if (!savedComparePrice && priceCompare) {
        const comparePriceData = priceCompare.getAttribute('data-compare-price') || priceCompare.getAttribute('data-price');
        if (comparePriceData) {
          savedComparePrice = parseInt(comparePriceData);
        }
      }
      
      // Check if item has line level discounts
      const hasDiscount = item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0;
      const originalPrice = item.original_price;
      const finalPrice = item.final_price;
      
      // Check if variant has compare_at_price in API response
      const hasComparePriceInAPI = item.variant && item.variant.compare_at_price && 
                              item.variant.compare_at_price > finalPrice;
      
      // Use compare_at_price from API if available, otherwise use saved from DOM, or original_price for discounts
      let comparePriceValue = null;
      if (hasComparePriceInAPI) {
        comparePriceValue = item.variant.compare_at_price;
      } else if (savedComparePrice && savedComparePrice > finalPrice) {
        comparePriceValue = savedComparePrice;
      } else if (hasDiscount || originalPrice > finalPrice) {
        comparePriceValue = originalPrice;
      }
      
      // Check if compare price should be shown based on section setting
      const showComparePriceSetting = this.getAttribute('data-show-compare-price');
      const isComparePriceEnabled = showComparePriceSetting === null || showComparePriceSetting === '' || showComparePriceSetting === 'true';
      
      // Show compare price only if setting is enabled
      const shouldShowComparePrice = isComparePriceEnabled && ((hasComparePriceInAPI || (savedComparePrice && savedComparePrice > finalPrice)) || (hasDiscount && originalPrice > finalPrice));
      
      // If compare price setting is disabled, always show regular price (even with discount or compare price in data)
      if (!isComparePriceEnabled) {
        // Hide sale price and compare price when setting is disabled
        if (priceSale) {
          priceSale.style.display = 'none';
        }
        if (priceCompare) {
          priceCompare.style.display = 'none';
        }
        
        // Show regular price (ordinary color, not red)
        if (!priceRegular) {
          const regularPrice = document.createElement('span');
          regularPrice.className = 'cart-new__price-regular';
          regularPrice.textContent = this.formatMoney(finalPrice);
          if (priceSale && priceSale.parentNode) {
            priceContainer.insertBefore(regularPrice, priceSale);
          } else if (priceCompare && priceCompare.parentNode) {
            priceContainer.insertBefore(regularPrice, priceCompare);
          } else {
            priceContainer.appendChild(regularPrice);
          }
        } else {
          priceRegular.textContent = this.formatMoney(finalPrice);
          priceRegular.style.display = 'inline';
        }
        
        // Still show discount badges even if compare price is disabled
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
          const discountText = item.line_level_discount_allocations
            .map(d => `${d.discount_application.title} (-${this.formatMoney(d.amount)})`)
            .join(', ');
          
          let discountBadge = itemRow.querySelector('.cart-new__discount-badge');
          if (!discountBadge && priceContainer) {
            discountBadge = document.createElement('span');
            discountBadge.className = 'cart-new__discount-badge';
            discountBadge.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <g clip-path="url(#clip0_2486_1092)">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.06792 0.313109C4.35156 0.303328 4.62653 0.411715 4.82722 0.6124L9.38759 5.17278C9.78747 5.57264 9.78747 6.22095 9.38759 6.62083L6.62083 9.38761C6.22095 9.78747 5.57264 9.78747 5.17278 9.38759L0.6124 4.82722C0.411715 4.62653 0.303328 4.35156 0.313109 4.06791L0.405336 1.39336C0.423879 0.855556 0.855559 0.423879 1.39336 0.405334L4.06792 0.313109ZM4.38528 1.05434C4.30709 0.976154 4.19997 0.933926 4.08945 0.937737L1.4149 1.02996C1.20537 1.03719 1.03719 1.20537 1.02996 1.4149L0.937737 4.08945C0.933926 4.19995 0.976154 4.30709 1.05434 4.38528L5.61472 8.94566C5.77051 9.10145 6.02309 9.10145 6.17887 8.94566L8.94566 6.17887C9.10145 6.02309 9.10145 5.77051 8.94566 5.61472L4.38528 1.05434ZM2.28023 2.28023C2.61064 1.94983 3.14634 1.94983 3.47675 2.28023C3.80717 2.61064 3.80717 3.14634 3.47675 3.47675C3.14634 3.80717 2.61064 3.80717 2.28023 3.47675C1.94983 3.14634 1.94983 2.61064 2.28023 2.28023ZM3.03481 2.72219C2.94848 2.63584 2.80852 2.63584 2.72219 2.72219C2.63584 2.80852 2.63584 2.94848 2.72219 3.03481C2.80852 3.12114 2.94848 3.12114 3.03481 3.03481C3.12114 2.94848 3.12114 2.80852 3.03481 2.72219Z" fill="#505050"/>
                </g>
                <defs>
                  <clipPath id="clip0_2486_1092_${item.key.replace(/[^a-zA-Z0-9]/g, '')}">
                    <rect width="10" height="10" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              ${discountText}
            `;
            const insertAfter = priceRegular || priceSale || priceCompare;
            if (insertAfter && insertAfter.nextSibling) {
              priceContainer.insertBefore(discountBadge, insertAfter.nextSibling);
            } else {
              priceContainer.appendChild(discountBadge);
            }
          } else if (discountBadge) {
            const svg = discountBadge.querySelector('svg');
            if (svg) {
              discountBadge.innerHTML = svg.outerHTML + ' ' + discountText;
            } else {
              discountBadge.innerHTML = discountText;
            }
            discountBadge.style.display = 'inline';
          }
        }
        
        return; // Exit early - regular price shown, don't process sale price logic
      }
      
      // Compare price setting is enabled - show sale price and compare price as needed
      // Show sale price if we have discount OR if compare price exists
      const shouldShowSalePrice = hasDiscount || (hasComparePriceInAPI || (savedComparePrice && savedComparePrice > finalPrice));
      
      if (shouldShowSalePrice && (shouldShowComparePrice || hasDiscount)) {
        // Item has discount or compare price - show sale and compare prices
        if (!priceSale) {
          // Create sale price element
          const salePrice = document.createElement('span');
          salePrice.className = 'cart-new__price-sale';
          salePrice.textContent = this.formatMoney(finalPrice);
          if (priceRegular && priceRegular.parentNode) {
            priceContainer.insertBefore(salePrice, priceRegular);
          } else {
            priceContainer.appendChild(salePrice);
          }
        } else {
          priceSale.textContent = this.formatMoney(finalPrice);
          priceSale.style.display = 'inline';
        }
        
        // Show compare price only if setting is enabled and we have a value
        if (shouldShowComparePrice && comparePriceValue) {
          if (!priceCompare) {
            // Create compare price element
            const comparePriceEl = document.createElement('span');
            comparePriceEl.className = 'cart-new__price-compare';
            comparePriceEl.setAttribute('data-compare-price', comparePriceValue);
            comparePriceEl.setAttribute('data-price', comparePriceValue);
            comparePriceEl.textContent = this.formatMoney(comparePriceValue);
            if (priceSale && priceSale.nextSibling) {
              priceContainer.insertBefore(comparePriceEl, priceSale.nextSibling);
            } else {
              priceContainer.appendChild(comparePriceEl);
            }
          } else {
            priceCompare.textContent = this.formatMoney(comparePriceValue);
            priceCompare.setAttribute('data-compare-price', comparePriceValue);
            priceCompare.setAttribute('data-price', comparePriceValue);
            priceCompare.style.display = 'inline';
          }
        } else if (priceCompare) {
          // Hide compare price if it shouldn't be shown
          priceCompare.style.display = 'none';
        }
        
        // Also update row data attribute to preserve compare price
        if (comparePriceValue) {
          itemRow.setAttribute('data-compare-price', comparePriceValue);
        }
        
        if (priceRegular) {
          priceRegular.style.display = 'none';
        }
        
        // Update or create discount badge
        if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
          const discountText = item.line_level_discount_allocations
            .map(d => `${d.discount_application.title} (-${this.formatMoney(d.amount)})`)
            .join(', ');
          
          if (!discountBadge && priceContainer) {
            // Create discount badge with SVG icon
            discountBadge = document.createElement('span');
            discountBadge.className = 'cart-new__discount-badge';
            discountBadge.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                <g clip-path="url(#clip0_2486_1092)">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.06792 0.313109C4.35156 0.303328 4.62653 0.411715 4.82722 0.6124L9.38759 5.17278C9.78747 5.57264 9.78747 6.22095 9.38759 6.62083L6.62083 9.38761C6.22095 9.78747 5.57264 9.78747 5.17278 9.38759L0.6124 4.82722C0.411715 4.62653 0.303328 4.35156 0.313109 4.06791L0.405336 1.39336C0.423879 0.855556 0.855559 0.423879 1.39336 0.405334L4.06792 0.313109ZM4.38528 1.05434C4.30709 0.976154 4.19997 0.933926 4.08945 0.937737L1.4149 1.02996C1.20537 1.03719 1.03719 1.20537 1.02996 1.4149L0.937737 4.08945C0.933926 4.19995 0.976154 4.30709 1.05434 4.38528L5.61472 8.94566C5.77051 9.10145 6.02309 9.10145 6.17887 8.94566L8.94566 6.17887C9.10145 6.02309 9.10145 5.77051 8.94566 5.61472L4.38528 1.05434ZM2.28023 2.28023C2.61064 1.94983 3.14634 1.94983 3.47675 2.28023C3.80717 2.61064 3.80717 3.14634 3.47675 3.47675C3.14634 3.80717 2.61064 3.80717 2.28023 3.47675C1.94983 3.14634 1.94983 2.61064 2.28023 2.28023ZM3.03481 2.72219C2.94848 2.63584 2.80852 2.63584 2.72219 2.72219C2.63584 2.80852 2.63584 2.94848 2.72219 3.03481C2.80852 3.12114 2.94848 3.12114 3.03481 3.03481C3.12114 2.94848 3.12114 2.80852 3.03481 2.72219Z" fill="#505050"/>
                </g>
                <defs>
                  <clipPath id="clip0_2486_1092_${item.key.replace(/[^a-zA-Z0-9]/g, '')}">
                    <rect width="10" height="10" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              ${discountText}
            `;
            // Insert badge after price elements
            const insertAfter = priceCompare || priceSale || priceRegular;
            if (insertAfter && insertAfter.nextSibling) {
              priceContainer.insertBefore(discountBadge, insertAfter.nextSibling);
            } else {
              priceContainer.appendChild(discountBadge);
            }
          } else if (discountBadge) {
            // Update existing badge - preserve SVG, update text
            const svg = discountBadge.querySelector('svg');
            if (svg) {
              discountBadge.innerHTML = svg.outerHTML + ' ' + discountText;
            } else {
              discountBadge.innerHTML = discountText;
            }
            discountBadge.style.display = 'inline';
          }
        }
      } else {
        // No discount and no compare price - show regular price only
        if (priceCompare) {
          priceCompare.style.display = 'none';
        }
        if (priceSale) {
          priceSale.style.display = 'none';
        }
        if (discountBadge) {
          discountBadge.style.display = 'none';
        }
        
        // Always ensure regular price is visible
        if (!priceRegular) {
          // Create regular price element if it doesn't exist
          const regularPrice = document.createElement('span');
          regularPrice.className = 'cart-new__price-regular';
          regularPrice.textContent = this.formatMoney(finalPrice);
          // Remove any existing price elements first
          if (priceSale) priceSale.remove();
          if (priceCompare) priceCompare.remove();
          priceContainer.appendChild(regularPrice);
        } else {
          priceRegular.textContent = this.formatMoney(finalPrice);
          priceRegular.style.display = 'inline';
        }
        
        // Ensure at least one price is visible
        const visiblePrices = priceContainer.querySelectorAll('.cart-new__price-regular[style*="inline"], .cart-new__price-sale[style*="inline"]');
        if (visiblePrices.length === 0 && !priceContainer.querySelector('.cart-new__price-regular')) {
          // Fallback: create regular price if nothing is visible
          const fallbackPrice = document.createElement('span');
          fallbackPrice.className = 'cart-new__price-regular';
          fallbackPrice.textContent = this.formatMoney(finalPrice);
          priceContainer.appendChild(fallbackPrice);
        }
      }
    });

    // Remove items that are no longer in cart (only if we're sure they're gone)
    // Do this carefully - only remove if the cart data confirms the item is gone
    const existingRows = Array.from(this.querySelectorAll('.cart-new__item, tr[data-line-key]'));
    existingRows.forEach(row => {
      const lineKey = row.dataset.lineKey || row.getAttribute('data-line-key');
      if (lineKey && !existingLineKeys.has(lineKey)) {
        // Double-check: item should not exist in items array
        const itemStillExists = items.some(item => item.key === lineKey);
        if (!itemStillExists) {
          // Item is truly gone, remove it
          row.remove();
        }
      }
    });
  }

  updateCartTotals(cartData) {
    // Calculate total discount amount to determine if we should show subtotal
    let totalDiscount = 0;
    
    // Calculate line-level discounts
    if (cartData.items && cartData.items.length > 0) {
      cartData.items.forEach(item => {
        const originalLinePrice = item.original_line_price || item.final_line_price || 0;
        const finalLinePrice = item.final_line_price || 0;
        totalDiscount += (originalLinePrice - finalLinePrice);
      });
    }
    
    // Add cart-level discounts
    if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
      cartData.cart_level_discount_applications.forEach(discount => {
        totalDiscount += (discount.total_allocated_amount || 0);
      });
    }

    // Show or hide subtotal based on whether there are discounts
    const subtotalRow = this.querySelector('#cart-new-subtotal-row');
    if (subtotalRow) {
      if (totalDiscount > 0) {
        // Calculate and show original subtotal
        let originalSubtotal = 0;
        if (cartData.items && cartData.items.length > 0) {
          originalSubtotal = cartData.items.reduce((sum, item) => {
            return sum + (item.original_line_price || item.final_line_price || 0);
          }, 0);
        }
        
        const subtotalEl = this.querySelector('#cart-new-subtotal');
        if (subtotalEl) {
          subtotalEl.textContent = this.formatMoney(originalSubtotal);
        }
        subtotalRow.style.display = '';
      } else {
        subtotalRow.style.display = 'none';
      }
    }

    // Update discount rows
    this.updateDiscountRows(cartData);

    // Update total
    const totalEl = this.querySelector('#cart-new-total');
    if (totalEl) {
      totalEl.textContent = this.formatMoneyWithCurrency(cartData.total_price);
    }
  }

  updateDiscountRows(cartData) {
    const totalsContainer = this.querySelector('.cart-new__totals');
    if (!totalsContainer) return;

    // Remove existing discount rows (except grand total)
    const existingDiscountRows = totalsContainer.querySelectorAll('.cart-new__total-row--discount');
    existingDiscountRows.forEach(row => row.remove());

    // Calculate total discount amount including:
    // 1. Line-level discounts (difference between original and final line prices)
    // 2. Cart-level discounts
    let totalDiscount = 0;
    
    // Calculate line-level discounts
    if (cartData.items && cartData.items.length > 0) {
      cartData.items.forEach(item => {
        const originalLinePrice = item.original_line_price || item.final_line_price || 0;
        const finalLinePrice = item.final_line_price || 0;
        totalDiscount += (originalLinePrice - finalLinePrice);
      });
    }
    
    // Add cart-level discounts
    if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
      cartData.cart_level_discount_applications.forEach(discount => {
        totalDiscount += (discount.total_allocated_amount || 0);
      });
    }

    // Show or hide discount row
    const grandTotalRow = totalsContainer.querySelector('.cart-new__total-row--grand');
    
    if (totalDiscount > 0) {
      // Create discount row if it doesn't exist or was removed
      const discountRow = document.createElement('div');
      discountRow.className = 'cart-new__total-row cart-new__total-row--discount';
      discountRow.innerHTML = `
        <span class="cart-new__total-label">Discount</span>
        <span class="cart-new__total-value cart-new__total-value--discount" id="cart-new-discount">-${this.formatMoney(totalDiscount)}</span>
      `;
      
      if (grandTotalRow) {
        totalsContainer.insertBefore(discountRow, grandTotalRow);
      } else {
        totalsContainer.appendChild(discountRow);
      }
    }
  }

  updateFreeShipping(cartData) {
    // Trigger free shipping bar update if it exists
    const freeShippingBar = document.querySelector('free-shipping-bar');
    if (freeShippingBar && typeof freeShippingBar.update === 'function') {
      freeShippingBar.update(cartData);
    }
    
    // Update free shipping bar in the sidebar if exists
    const freeShippingContainer = this.querySelector('#cart-new-free-shipping');
    if (freeShippingContainer && cartData.requires_shipping) {
      // Trigger update for free shipping component
      const freeShippingComponent = freeShippingContainer.querySelector('free-shipping-bar');
      if (freeShippingComponent) {
        // The component should listen to cart:change events
      }
    }
  }

  reinitializeQuantityControls() {
    // Re-bind events for new quantity controls
    // The event listeners are already bound via bindEvents, but we may need to reinitialize
    // quantity-input custom elements if they exist
    
    // Ensure all inputs and buttons have correct data-line-key from their parent row
    const allRows = this.querySelectorAll('.cart-new__item, tr[data-line-key]');
    allRows.forEach(row => {
      const rowKey = row.getAttribute('data-line-key');
      if (!rowKey) return;
      
      // Update all inputs
      const inputs = row.querySelectorAll('.cart-new__quantity-input');
      inputs.forEach(input => {
        input.setAttribute('data-line-key', rowKey);
      });
      
      // Update all buttons
      const buttons = row.querySelectorAll('.cart-new__quantity-btn, .cart-new__remove-btn');
      buttons.forEach(btn => {
        btn.setAttribute('data-line-key', rowKey);
      });
    });
    
    const quantityInputs = this.querySelectorAll('.cart-new__quantity-input');
    quantityInputs.forEach(input => {
      // If using custom elements, reinitialize them
      if (input.is && input.is === 'quantity-input') {
        // The custom element should handle its own initialization
      }
    });
  }

  formatMoney(cents) {
    // Round to nearest dollar (no decimals)
    const dollars = Math.round(cents / 100);
    
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      // Use Shopify's format but with rounded amount
      const roundedCents = dollars * 100;
      let formatted = Shopify.formatMoney(roundedCents, window.money_format || '${{amount}}');
      // Remove all decimal parts (everything after the dot)
      formatted = formatted.replace(/\.\d+/g, '');
      return formatted;
    }
    return '$' + dollars;
  }

  formatMoneyWithCurrency(cents) {
    // Round to nearest dollar (no decimals)
    const dollars = Math.round(cents / 100);
    
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      // Use Shopify's format but with rounded amount
      const roundedCents = dollars * 100;
      let formatted = Shopify.formatMoney(roundedCents, window.money_format || '${{amount}} USD');
      // Remove all decimal parts (everything after the dot)
      formatted = formatted.replace(/\.\d+/g, '');
      return formatted;
    }
    return '$' + dollars + ' USD';
  }
}

// Register the custom element
if (!customElements.get('cart-new-section')) {
  customElements.define('cart-new-section', CartNew);
}

